import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { submitRequest } from '../../redux/slices/sellerRegSlice';
import {
  srOnlySx,
  dialogPaperSx,
  dialogHeaderSx,
  dialogTitleSx,
  dialogCloseButtonSx,
  dialogContentSx,
  dialogBodySx,
  dialogRadioSx,
  dialogRadioLabelSx,
  dialogActionsSx
} from './SellerRegDialog.styles';

// ─── Props ────────────────────────────────────────────────────────────────────

interface SellerRegDialogProps {
  open: boolean;
  title: string;
  body: string;
  handleOnClose: () => void;
  /** 'confirm' (default) shows radio options + action buttons; 'alert' shows title + close + body only */
  variant?: 'confirm' | 'alert';
  // Required only for variant="confirm"
  isSubmitting?: boolean;
  payload?: any;
  /** [submit-option-label, redirect-option-label] */
  options?: [string, string];
  back?: string;
  confirm?: string;
  navigateURL?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SellerRegDialog = ({
  open,
  variant = 'confirm',
  isSubmitting = false,
  payload,
  title,
  body,
  options,
  back,
  confirm,
  handleOnClose,
  navigateURL = ''
}: SellerRegDialogProps) => {
  const isAlert = variant === 'alert';
  const titleId = useId();
  const bodyId = useId();
  const titleRef = useRef<HTMLDivElement>(null);
  const [selectedOption, setSelectedOption] = useState('');
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();

  // Reset selection each time the dialog opens
  useEffect(() => {
    if (open) setSelectedOption('');
  }, [open]);

  // Move focus to the title when the dialog opens so screen readers announce it.
  // A `key` on the Dialog (below) remounts it when the variant changes, so this
  // runs fresh for confirm → alert and only the current content is announced.
  useEffect(() => {
    if (!open) return;
   const frameId = requestAnimationFrame(() => titleRef.current?.focus());
    return () => cancelAnimationFrame(frameId);
  }, [open]);

  // Prevent backdrop click from closing the dialog; allow Escape (WCAG SC 2.1.2)
  const handleClose = (_: unknown, reason?: string) => {
    if (reason === 'backdropClick') return;
    handleOnClose();
  };

  const handleConfirm = () => {
    if (!options || isSubmitting) return;
    if (selectedOption === options[1]) {
      navigate(`/${i18n.language}${navigateURL}`);
    } else {
      dispatch(submitRequest(payload));
    }
  };

  return (
    <Dialog
      key={variant}
      open={open}
      onClose={handleClose}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      PaperProps={{ref: titleRef, sx: dialogPaperSx, tabIndex:-1}}
      maxWidth={false}
    >
      
      <Box sx={{display: 'flex',  flexDirection: 'column', height: '100%', gap: 2}}> 

      {/* ── Header ── */}
      <Box sx={dialogHeaderSx}>
        <Box
          
          id={titleId}
          aria-label={variant === 'alert' ? `${title}` : `${title} Dialog`}
          sx={dialogTitleSx}
        >
          {title}
        </Box>
        <IconButton
          aria-label={t('registrationStepsData.dialog.closeAriaLabel')}
          onClick={handleOnClose}
          sx={dialogCloseButtonSx}
          disableRipple
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Body ── */}
      <Box sx={dialogContentSx}>
        <Typography id={bodyId} sx={dialogBodySx}>
          {body}
        </Typography>
        {!isAlert && options && (
          <FormControl component="fieldset">
            {/* Visually-hidden legend for screen readers */}
            <FormLabel component="legend" sx={srOnlySx}>
              {t('registrationStepsData.dialog.radioLabel')}
            </FormLabel>
            <RadioGroup
              value={selectedOption}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedOption(e.target.value)}
            >
              <FormControlLabel
                value={options[0]}
                control={<Radio  disableRipple sx={dialogRadioSx} />}
                label={options[0]}
                sx={dialogRadioLabelSx}
              />
              <FormControlLabel
                value={options[1]}
                control={<Radio disableRipple sx={dialogRadioSx} />}
                label={options[1]}
                sx={dialogRadioLabelSx}
              />
            </RadioGroup>
          </FormControl>
        )}
      </Box>
      </Box>

      {/* ── Actions ── */}
      {!isAlert && (
        <Box sx={dialogActionsSx}>
          <Button variant="secondary" size="md" onClick={handleOnClose}>
            {back}
          </Button>
          {/* Stays focusable while submitting so focus doesn't bounce back to the
              dialog and re-announce it; double-submit is guarded in handleConfirm */}
          <Button
            variant="primary"
            size="md"
            disabled={!selectedOption}
            onClick={handleConfirm}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {confirm}
          </Button>
        </Box>
      )}
      
    </Dialog>
  );
};

export default SellerRegDialog;
